/**
 * CongestionCard — iter1057 — кратка проверка за застой (сърдечна недостатъчност):
 * 7 въпроса + автоматична проверка на теглото (+2 кг за 3 дни = течност).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { ArrowRight, Droplets, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LEVEL_STYLE = {
  suspect: { bg: "bg-rose-50", dark: "bg-rose-900/25", border: "border-rose-200", darkBorder: "border-rose-500/40" },
  watch: { bg: "bg-amber-50", dark: "bg-amber-900/20", border: "border-amber-200", darkBorder: "border-amber-500/30" },
  ok: { bg: "bg-emerald-50", dark: "bg-emerald-900/20", border: "border-emerald-200", darkBorder: "border-emerald-500/30" },
  no_data: { bg: "bg-slate-50", dark: "bg-slate-800/40", border: "border-slate-200", darkBorder: "border-slate-700" },
};

export const CongestionCard = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openWhy, setOpenWhy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/cardio/congestion`)
      .then(({ data }) => { setData(data); setSel(data.selected || []); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/cardio/congestion`, { items: sel });
      setData(data);
      setEditing(false);
      toast.success("Кратката проверка е записана.");
    } catch {
      toast.error("Записът не мина.");
    } finally {
      setSaving(false);
    }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${card} flex justify-center`} data-testid="congestion-loading">
      <Loader2 className="w-5 h-5 animate-spin text-red-500" /></div>;
  }
  if (!data) return null;
  const st = LEVEL_STYLE[data.level] || LEVEL_STYLE.no_data;

  return (
    <div className={`p-5 rounded-2xl border ${card}`} data-testid="congestion-card">
      <div className="flex items-center gap-2 mb-2">
        <Droplets className="w-4 h-4 text-red-500" />
        <h2 className="font-bold text-sm">Кратка проверка за задържане на течност</h2>
        <span className="ml-auto text-[11px] opacity-60 tabular-nums" data-testid="congestion-points">
          {data.points}/{data.max_points}
        </span>
      </div>

      <div className={`p-3 rounded-xl mb-2 border ${isDark ? `${st.dark} ${st.darkBorder}` : `${st.bg} ${st.border}`}`}
           data-testid="congestion-verdict">
        <div className="text-xs font-bold" data-testid="congestion-level">{data.label_bg}</div>
        <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{data.text_bg}</div>
      </div>

      {data.weight && (
        <div className={`p-2.5 rounded-xl mb-2 text-[11px] leading-relaxed ${data.weight.flag
          ? (isDark ? "bg-rose-900/25" : "bg-rose-50") : box}`} data-testid="congestion-weight">
          <span className="font-bold">Тегло: {data.weight.from_kg} → {data.weight.to_kg} кг. </span>
          {data.weight.text_bg}
        </div>
      )}

      {data.nudge_bg && (
        <div className={`p-2.5 rounded-xl mb-2 text-[11px] leading-relaxed ${isDark ? "bg-violet-900/25 border border-violet-500/30" : "bg-violet-50 border border-violet-200"}`}
             data-testid="congestion-nudge">
          <span className="font-bold">Напомняне: </span>{data.nudge_bg}
        </div>
      )}

      {!editing ? (
        <button onClick={() => setEditing(true)} data-testid="congestion-edit"
                className="w-full py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold">
          {data.has_data ? "Обнови отговорите" : "Попълнете проверката (2 минути)"}
        </button>
      ) : (
        <div className="space-y-1.5" data-testid="congestion-form">
          {data.items.map((it) => {
            const on = sel.includes(it.key);
            return (
              <div key={it.key} className={`rounded-xl overflow-hidden ${box}`}>
                <div className="flex items-center gap-2 p-2.5">
                  <button onClick={() => setSel(on ? sel.filter((k) => k !== it.key) : [...sel, it.key])}
                          data-testid={`congestion-item-${it.key}`}
                          className={`w-4 h-4 rounded shrink-0 border ${on ? "bg-red-600 border-red-600" : isDark ? "border-slate-600" : "border-slate-400"}`} />
                  <span className="text-[11px] flex-1 min-w-0">{it.label_bg}</span>
                  <button onClick={() => setOpenWhy(openWhy === it.key ? null : it.key)}
                          data-testid={`congestion-why-${it.key}`}
                          className="text-[10px] opacity-50 hover:opacity-100 shrink-0">защо?</button>
                </div>
                {openWhy === it.key && (
                  <div className="px-2.5 pb-2.5 text-[10px] opacity-80 leading-relaxed">{it.why_bg}</div>
                )}
              </div>
            );
          })}
          <button onClick={save} disabled={saving} data-testid="congestion-save"
                  className="w-full py-2 rounded-lg bg-red-600 text-white text-[11px] font-bold disabled:opacity-60">
            {saving ? "Записвам…" : "Запиши"}
          </button>
        </div>
      )}

      <p className="text-[10px] opacity-65 mt-2 leading-relaxed" data-testid="congestion-compensation">
        {data.compensation_bg}
      </p>

      {data.links?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="congestion-links">
          {data.links.map((l, i) => (
            <button key={i} onClick={() => navigate(l.route)} data-testid={`congestion-link-${i}`}
                    className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
              {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CongestionCard;
